/**
 * This component is used around the site to display a donate link which,
 * when clicked, opens our donation page (donate.khanacademy.org) in
 * a new window/tab.
 */
import {t} from "@lingui/core/macro";
import * as React from "react";

import type {StyleType} from "@khanacademy/wonder-blocks-core";
import Link from "@khanacademy/wonder-blocks-link";
import {actionStyles} from "@khanacademy/wonder-blocks-styles";
import {RequestInfo, useUserAttributes} from "@khan/shared-core";

import getClassyCampaignURL, {
    type ClassyPassThroughParams,
} from "#donate-package/classy.ts";

type Props = {
    children?: string;
    // Whether the link is contained within surrounding text.
    // This adds an underline.
    inline?: boolean;
    // Whether the link is displayed on a dark background.
    light?: boolean;
    // aphrodite styles
    style?: StyleType;
    // react key
    key?: string;
    // params that get passed-through to classy
    // c_src will default to the users kaid
    urlParams?: ClassyPassThroughParams;
    // Wonderblocks safeWithNav function
    safeWithNav?: () => Promise<unknown>;
    // QA Test id
    testId?: string;
};

export default function DonateLink(
    props: Props,
): React.ReactElement<React.ComponentProps<typeof Link>> {
    const {children, light, style, urlParams, safeWithNav, ...rest} = props;

    const userAttributes = useUserAttributes();

    return (
        <Link
            target="_blank"
            href={getClassyCampaignURL(RequestInfo.kaLocale, {
                c_src: userAttributes?.kaid ?? null,
                ...urlParams,
            })}
            style={[style, light && actionStyles.inverse]}
            {...(safeWithNav ? {safeWithNav: safeWithNav} : null)}
            {...rest}
        >
            {children ?? t`Donate`}
        </Link>
    );
}
